import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, View } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import cs from 'classnames';
import { addDays, startOfDay } from 'date-fns';

import { useMobileDesign } from 'hooks/useMobileDesign';

import {
  CalendarEvent,
  InterviewSlotSerialized,
  SlotType
} from 'types/interviewSlots';

import { InterviewSchedulerView } from 'constants/enums';

import { selectAppLocale } from '../../features/locale/localeSelectors';
import MonthEvent from './components/MonthEvent/MonthEvent';
import Toolbar from './components/Toolbar/Toolbar';
import {
  DAYS_AHEAD,
  calendarSettings,
  getCalendarFormats,
  getCalendarMessages
} from './config/calendarConfig';
import { useSchedulerEvents } from './hooks/useSchedulerEvents';
import { useSlotValidation } from './hooks/useSlotValidation';
import { getDateFnsLocale } from './utils/calendarLocaleMap';
import {
  calendarMaxDate,
  calendarMinDate,
  localizer
} from './utils/calendarLocalizer';
import { collapseEventsToOnePerDay } from './utils/collapseEventsToOnePerDay';
import { resolveViewForLayout } from './utils/resolveViewForLayout';

interface InterviewSchedulerProps {
  proposedSlots?: InterviewSlotSerialized[];
  // TODO: remove when sidebar uses container-query layout instead of prop override
  /** Narrow contexts (e.g. sidebar) match mobile Day/Month UX without viewport breakpoint. */
  forceMobileLayout?: boolean;
}

const InterviewScheduler: FC<InterviewSchedulerProps> = ({
  proposedSlots = [],
  forceMobileLayout = false
}) => {
  const [view, setView] = useState<View>(calendarSettings.defaultDesktopView);
  const [date, setDate] = useState(new Date());
  const { i18n } = useTranslation();
  const language = useSelector(selectAppLocale);

  const rawIsMobile = useMobileDesign();
  const shouldUseMobileCalendar = forceMobileLayout || rawIsMobile;

  const resolvedView = useMemo(
    () => resolveViewForLayout(shouldUseMobileCalendar, view),
    [shouldUseMobileCalendar, view]
  );

  const today = useMemo(() => startOfDay(new Date()), []);
  const maxSelectableDate = useMemo(
    () => startOfDay(addDays(today, DAYS_AHEAD)),
    [today]
  );

  useEffect(() => {
    if (resolvedView !== view) {
      setView(resolvedView);
    }
  }, [resolvedView, view]);

  const { isSlotDisabled, dayPropGetter, slotPropGetter } = useSlotValidation({
    maxSelectableDate,
    shadeWeekendsOnlyInMonthView: resolvedView === InterviewSchedulerView.Month
  });

  const currentLocale = useMemo(() => getDateFnsLocale(language), [language]);

  const calendarFormats = useMemo(
    () => getCalendarFormats(currentLocale, shouldUseMobileCalendar),
    [currentLocale, shouldUseMobileCalendar]
  );

  const { events, handleSelectEvent, handleSelectSlot } = useSchedulerEvents({
    isMobile: shouldUseMobileCalendar,
    maxSelectableDate,
    isSlotDisabled,
    proposedSlots
  });

  const calendarEvents = useMemo(() => {
    if (resolvedView !== InterviewSchedulerView.Month) {
      return events;
    }
    return collapseEventsToOnePerDay(events);
  }, [events, resolvedView]);

  const eventPropGetter = useCallback((event: CalendarEvent) => {
    const className =
      event.type === SlotType.Proposed ? 'event-proposed' : 'event-selected';
    return {
      className
    };
  }, []);

  return (
    // @ts-expect-error — react-big-calendar: Calendar props typing incomplete vs runtime (selectable union, accessors, layouts)
    <Calendar
      localizer={localizer}
      culture={language}
      events={calendarEvents}
      startAccessor="start"
      endAccessor="end"
      className={cs('interview-scheduler', {
        'is-mobile': shouldUseMobileCalendar,
        'view-week': resolvedView === InterviewSchedulerView.Week,
        'view-day': resolvedView === InterviewSchedulerView.Day
      })}
      onSelectEvent={handleSelectEvent}
      onSelectSlot={(slotInfo) =>
        handleSelectSlot(slotInfo, resolvedView, setView, setDate)
      }
      selectable="ignoreEvents"
      view={resolvedView}
      onView={setView}
      date={date}
      onNavigate={setDate}
      defaultView={
        shouldUseMobileCalendar
          ? calendarSettings.defaultMobileView
          : calendarSettings.defaultDesktopView
      }
      views={
        shouldUseMobileCalendar
          ? calendarSettings.mobileViews
          : calendarSettings.desktopViews
      }
      min={calendarMinDate}
      max={calendarMaxDate}
      step={calendarSettings.step}
      timeslots={calendarSettings.timeslots}
      dayPropGetter={dayPropGetter}
      slotPropGetter={slotPropGetter}
      eventPropGetter={eventPropGetter}
      scrollToTime={calendarMinDate}
      formats={calendarFormats}
      messages={getCalendarMessages(i18n)}
      components={{
        toolbar: (toolbarProps) => (
          <Toolbar {...toolbarProps} isMobile={shouldUseMobileCalendar} />
        ),
        month: {
          event: MonthEvent
        }
      }}
      popup={false}
      longPressThreshold={
        shouldUseMobileCalendar
          ? calendarSettings.longPressThresholdMobile
          : calendarSettings.longPressThresholdDesktop
      }
      dayLayoutAlgorithm="no-overlap"
    />
  );
};

export default InterviewScheduler;
